import { useEffect, useMemo, useState } from "react";
import { resolveSurfPreviewImageUrl } from "@vida/surf-core";
import { createSurfApiConfig, ensureSurfClientConfig, getPdfPresignedUrl } from "../services/surfClientConfig";
import { assetCacheKey, getCachedAssetUrl } from "../utils/surfAssetCache";

/** Resolve stable thumbnail object URLs for arbitrary s3 keys (cached, no flicker). */
export function useSurfAssetThumbs(keys: string[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const fingerprint = useMemo(() => [...new Set(keys.filter(Boolean))].sort().join("\n"), [keys]);

  useEffect(() => {
    const unique = [...new Set(keys.filter((k) => k && k.includes("/")))];
    if (!unique.length) {
      setUrls({});
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await ensureSurfClientConfig();
        const cfg = createSurfApiConfig();
        const presigned = getPdfPresignedUrl();
        if (!presigned) return;
        const out: Record<string, string> = {};
        await Promise.all(
          unique.map(async (key) => {
            try {
              out[key] = await getCachedAssetUrl(assetCacheKey(key), async () => {
                const url = await resolveSurfPreviewImageUrl(presigned, () => cfg.getIdToken(), key);
                const res = await fetch(url, { cache: "no-store" });
                if (!res.ok) throw new Error(`Thumb ${res.status}`);
                return res.blob();
              });
            } catch {
              /* skip */
            }
          }),
        );
        if (!cancelled) setUrls(out);
      } catch {
        /* keep */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  return urls;
}
